/*
 * Developer  : Sakshi Bhise
 * Script     : SidebarFooterMenu.tsx
 * Date       : 2026-06-08
 * Purpose    : Contains the Settings and Language panels that slide open from
 *              the sidebar footer, plus a helper that builds the footer menu
 *              items (Settings and Languages buttons shown next to the user avatar).
 */

// ─── Imports ──────────────────────────────────────────────────────────────────

import { ChevronRight, Delete, Globe, Icon, Monitor, Moon, Settings, Sun, Trash2 } from "lucide-react"; // Icon library — provides small vector icons used throughout the UI
import { useState, useContext, useEffect } from "react";                                                // React tools: useState tracks changing values; useContext reads shared data; useEffect runs code when values change
import { useTheme, mergeStyles, DefaultButton } from "@fluentui/react";                                 // Fluent UI: useTheme provides brand colours; mergeStyles joins CSS classes; DefaultButton is a standard button
import { AppearanceOptionConfig, AppearancePicker, Button, SearchList, SearchOption } from "@teva/ui";  // Teva design system: appearance picker for light/dark/system and a searchable list component
import { getSidebarLayoutStyles } from "./Sidebarlayout.style";                                         // Local CSS-in-JS style definitions for the sidebar panels
import { AppStateContext } from "../../state/AppProvider";                                              // Global app state — holds user preferences such as language and permissions
import i18n from "../../i18n";                                                                          // Internationalisation engine — switches all translated text to a new language
import { AuthContext } from "../../state/AuthContext";                                                  // Authentication context — provides the user's access token for API calls
import { updateLanguage } from "../../api/api";                                                        // API call that saves the user's language preference to the server
import { useTranslation } from "react-i18next";                                                        // Hook that returns translated text strings for the currently active language
import { useAppTheme } from "../../state/ThemeContext";                                                 // Hook to read and change the app's visual theme (light / dark / system)


// ─── Component: SettingsPanel ─────────────────────────────────────────────────

// Shows the Settings panel with controls for Appearance (light/dark/system theme),
// Language selection, and a button to permanently delete all chat history.
export function SettingsPanel() {
    const theme = useTheme();                                                        // reads the current colour theme so styles can match light or dark mode
    const styles = getSidebarLayoutStyles(theme);                                    // builds the CSS style rules for this panel using the current theme
    const { state, dispatch }: any = useContext(AppStateContext);                    // reads global app state (user language, permissions, etc.) and provides a function to update it
    const { token } = useContext(AuthContext);                                       // the user's authentication token — required when making API calls
    const { t } = useTranslation();                                                  // returns translated text, e.g. t("settings") gives "Settings" in the active language
    const { preference: appearance, setPreference: setAppearance } = useAppTheme(); // current theme choice (light/dark/system) and a function to change it
    const [isLanguageOpen, setIsLanguageOpen] = useState(false);                    // tracks whether the language dropdown list is expanded or collapsed
    const [selectedKey, setSelectedKey] = useState<string | undefined>(
        state.userPreferredLanguage || state.userLanguage                            // start with the user's saved preference, falling back to their auto-detected language
    );

    const availableLanguages = state.availableLanguages || []; // all languages the app supports, or an empty list if none have loaded yet

    // Sorts and converts the language list into the format the dropdown expects.
    // English always appears first; the rest are sorted A–Z by display name.
    // Example: [{code:'en', name:'English'}, {code:'he', name:'Hebrew', native:'עברית'}]
    //          → [{key:'en', label:'English'}, {key:'he', label:'Hebrew', subLabel:'עברית'}]
    const dropdownOptions: SearchOption[] = availableLanguages.sort((a: any, b: any) => {
        if (a.code === 'en') return -1;            // English is always first
        if (b.code === 'en') return 1;
        return a.name.localeCompare(b.name);        // all other languages sorted alphabetically by name
    }).map((language: any) => ({
        key: language.code,        // short language code used internally, e.g. 'en', 'he'
        subLabel: language.native, // name written in the language's own script, e.g. 'עברית' for Hebrew
        label: language.name,      // English display name, e.g. 'Hebrew'
    }));

    // Find the dropdown entry that matches the currently selected language code
    const selectedOption = dropdownOptions.find(opt => opt.key === selectedKey);

    // Build the label shown in the collapsed language row.
    // English shows just "English"; other languages show "Hebrew / עברית"
    const displayText = selectedOption
        ? selectedOption.key === 'en' || selectedOption.key === 'en-GB'
            ? selectedOption.label                                          // just "English" for English variants
            : `${selectedOption.label} / ${selectedOption.subLabel}`        // "Hebrew / עברית" for other languages
        : 'English'; // fallback shown when no language is selected yet

    // Keeps the dropdown in sync if the language changes from somewhere else in the app
    useEffect(() => {
        const lang = state.userPreferredLanguage || state.userLanguage;
        if (lang) setSelectedKey(lang); // update the dropdown to reflect the new language
    }, [state.userPreferredLanguage, state.userLanguage]);

    // Called when the user picks a language from the dropdown.
    // Switches the app language immediately and saves the choice to the server.
    const handleLanguageChange = async (selectedCode: string) => {
        setSelectedKey(selectedCode);          // update the dropdown to show the chosen language
        i18n.changeLanguage(selectedCode);     // switch all translated text in the app immediately

        try {
            if (state?.isImpersonating) {
                // Support agents viewing another user's account — update state only, no API call
                dispatch({ type: "USER_PREFERRED_LANGUAGE", payload: selectedCode });
            } else {
                // Normal user — save the preference to the server so it is remembered after logout
                const response = await updateLanguage(
                    token,
                    { preferred_language: selectedCode, global_id: state.userGId }
                );
                if (!response.ok) {
                    console.error("Failed to update preferred language");
                } else {
                    dispatch({ type: "USER_PREFERRED_LANGUAGE", payload: selectedCode }); // confirm the change in global state
                }
            }
        } catch (error) {
            console.error("Error while updating preferred language:", error);
        }
    };

    // The three theme options shown in the appearance picker
    const DEFAULT_OPTIONS: AppearanceOptionConfig[] = [
        { value: 'light',  label: t("common.light"),  icon: (size: number) => <Sun size={size} /> },     // bright white theme
        { value: 'dark',   label: t("common.dark"),   icon: (size: number) => <Moon size={size} /> },    // dark background theme
        { value: 'system', label: t("common.system"), icon: (size: number) => <Monitor size={size} /> }, // matches the device's OS setting
    ];

    return (
        <div>
            <div className={mergeStyles(styles.settingsRow)}>

                {/* ── General section heading ── */}
                <p className={mergeStyles(styles.settingsSectionLabel)}>
                    {t("setting.general")}
                </p>

                {/* ── Appearance row: lets the user choose Light, Dark, or System theme ── */}
                <div className={mergeStyles(styles.settingsAppearanceRow)}>
                    <span className={mergeStyles(styles.settingsAppearanceLabel)}>{t("setting.appearance")}</span>
                    <AppearancePicker options={DEFAULT_OPTIONS} value={appearance} onChange={(v) => setAppearance(v as any)} />
                </div>

                {/* ── Language row: shows the current language and expands to a searchable list ── */}
                <div className={mergeStyles(styles.settingsLanguageRow)}>
                    <div className={mergeStyles(styles.settingsLanguageSecondRow)} >
                        <span className={mergeStyles(styles.settingsAppearanceLabel)}>{ t("common.languages") }</span>
                        {/* Clicking this area toggles the language list open or closed */}
                        <div onClick={() => setIsLanguageOpen(prev => !prev)} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#6b7280', cursor: 'pointer' }}>
                            {displayText} {/* shows the name of the currently selected language */}
                            {/* Arrow rotates to point upward when the list is open */}
                            <ChevronRight size={14} color="#9ca3af" style={{ transform: isLanguageOpen ? 'rotate(270deg)' : 'rotate(90deg)', transition: 'transform 0.2s' }} />
                        </div>
                    </div>
                    {/* Render the searchable language list only after the row has been clicked open */}
                    {isLanguageOpen && (<div>
                        <SearchList
                            selectedOptionKey={selectedKey}
                            onSelect={(option) => {
                                handleLanguageChange(option.key); // save the choice and switch the app language
                                setIsLanguageOpen(false);          // collapse the list after a selection is made
                            }}
                            options={dropdownOptions}
                            placeholder={t("common.search", { value: t("common.languages") })}
                            noResultText={t("common.noFound", { value: t("common.languages") })}
                        />
                    </div>)}
                </div>

                {/* ── Privacy & Data section heading ── */}
                <p className={mergeStyles(styles.settingsSectionLabel)}>
                    {t("common.privacyData")}
                </p>

                {/* ── Delete all chat history row ── */}
                <div className={mergeStyles(styles.settingsRowAlignCenter)}>
                    <div>
                        <div className={mergeStyles(styles.settingsChatHistoryTitle)}>  {t("chatHistoryPanel.contextMenu.clearAll")}</div>
                        <div className={mergeStyles(styles.settingsChatHistorySubtitle)}>{t("setting.deleteChatDetails")}</div>
                    </div>

                    {/* Delete button — tapping this will permanently remove all saved conversations */}
                    <DefaultButton
                        text={t("common.delete")}
                        onClick={() => { }}
                        className={mergeStyles(styles.deleteButtonStyles)}
                        iconProps={{
                            iconName: undefined, // we override the default Fluent icon with a custom lucide icon below
                        }}
                        onRenderIcon={() => <Trash2 size={12} />} // small bin/trash icon from the lucide library
                    />
                </div>

            </div>
        </div>
    );
}

// ─── Component: LanguagePanel ─────────────────────────────────────────────────

// Shows a full-height searchable list of every available language.
// When the user picks one, the app immediately switches to that language.
export function LanguagePanel() {
    const { t } = useTranslation();                                                  // returns translated text, e.g. t("settings") gives "Settings" in the active language
    const { state, dispatch }: any = useContext(AppStateContext); // reads global state (language list, current language) and allows updates
    const { token } = useContext(AuthContext);                    // authentication token — required to save the language preference to the server

    const [selectedKey, setSelectedKey] = useState<string | undefined>(
        state.userPreferredLanguage || state.userLanguage // start with the saved preference, falling back to the auto-detected language
    );

    const availableLanguages = state.availableLanguages || []; // all supported languages, or empty if not yet loaded

    // Sort and convert to the dropdown format — English first, the rest A–Z
    const dropdownOptions: SearchOption[] = availableLanguages.sort((a: any, b: any) => {
        if (a.code === 'en') return -1;
        if (b.code === 'en') return 1;
        return a.name.localeCompare(b.name);
    }).map((language: any) => ({
        key: language.code,        // short code, e.g. 'he'
        subLabel: language.native, // native name, e.g. 'עברית'
        label: language.name,      // English name, e.g. 'Hebrew'
    }));

    // Keep the highlighted row in sync when the language changes from elsewhere in the app
    useEffect(() => {
        const lang = state.userPreferredLanguage || state.userLanguage;
        if (lang) setSelectedKey(lang);
    }, [state.userPreferredLanguage, state.userLanguage]);

    // Called when the user taps a language row.
    // Switches the app language immediately and saves the choice to the server.
    const handleLanguageChange = async (selectedCode: string) => {
        setSelectedKey(selectedCode);      // highlight the chosen row
        i18n.changeLanguage(selectedCode); // switch all translated text to the new language

        try {
            if (state?.isImpersonating) {
                // Support agents — update state only, skip the server call
                dispatch({ type: "USER_PREFERRED_LANGUAGE", payload: selectedCode });
            } else {
                // Normal user — persist the preference so it survives after logout
                const response = await updateLanguage(
                    token,
                    { preferred_language: selectedCode, global_id: state.userGId }
                );
                if (!response.ok) {
                    console.error("Failed to update preferred language");
                } else {
                    dispatch({ type: "USER_PREFERRED_LANGUAGE", payload: selectedCode });
                }
            }
        } catch (error) {
            console.error("Error while updating preferred language:", error);
        }
    };

    return (
        // Outer box fills the panel height; the list inside can scroll when there are many languages
        <div style={{ padding: '8px', height: '100%', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
            <SearchList
                selectedOptionKey={selectedKey}
                onSelect={(option) => handleLanguageChange(option.key)} // switch language when the user taps a row
                options={dropdownOptions}
                style={{
                    container: { flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
                    list: { flex: 1, maxHeight: 'none', overflowY: 'auto' }, // allows the list to scroll when it is taller than the panel
                }}
                noResultText={t("common.noFound", { value: t("common.languages") })}
                placeholder={t("common.search", { value: t("common.languages") })}
            />
        </div>
    );
}

// ─── Helper: getFooterMenuItems ───────────────────────────────────────────────

// Builds the list of menu items shown when the user clicks the avatar in the sidebar footer.
// Returns two items: Settings and Languages. Each item has an icon, a label, and an action.
export function getFooterMenuItems(onUserSettings: () => void, onLanguages: () => void, currentLanguageNativeLabel: string) {
    const { t } = useTranslation(); // translated labels for the menu items
    return [
        // Settings item — opens the Settings panel when clicked
        //{ icon: <Settings size={16} />, label: t("common.settings", {value:""}), onClick: onUserSettings },
        {
            // Languages item — shows the current language's native name on the right, opens the language picker when clicked
            icon: <Globe size={16} />,
            label: (
                <span style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
                    <span>{t("common.languages")}</span>
                    <span style={{ color: "#9ca3af", fontWeight: 400 }}>{currentLanguageNativeLabel}</span> {/* e.g. 'עברית' when Hebrew is active */}
                </span>
            ) as any,
            onClick: onLanguages,
        },
    ];
}
